import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { User as FirebaseAuthUser } from 'firebase/auth';
import { FirebaseClient } from '../firebase/index';
import { User, FirestoreUser } from '../types/database';
import { UserRole, hasPermission, Permission, ResourceType } from '../types/shared';
import { useToast } from '../hooks/useToast';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<boolean>;
  logout: () => Promise<void>;
  register: (
    email: string, 
    password: string, 
    additionalData: { 
      name: string; 
      role: UserRole;
      metadata?: {
        phoneNumber?: string;
        timezone?: string;
      };
    }
  ) => Promise<boolean>;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  checkPermission: (resource: ResourceType, action: Permission) => boolean;
  refreshUser: () => Promise<void>;
}

const defaultContext: AuthContextType = {
  user: null,
  login: async () => false,
  logout: async () => {},
  register: async () => false,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  checkPermission: () => false,
  refreshUser: async () => {}
};

const AuthContext = createContext<AuthContextType>(defaultContext);

// Helper function to convert Firestore timestamps to Dates
function convertTimestamps(user: FirestoreUser): User {
  return {
    ...user,
    createdAt: user.createdAt.toDate(),
    updatedAt: user.updatedAt.toDate(),
    lastLoginAt: user.lastLoginAt?.toDate()
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { addToast } = useToast();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const currentUser = await FirebaseClient.getCurrentUser();
        if (currentUser) {
          const firestoreUser = await FirebaseClient.getUser(currentUser.uid);
          if (firestoreUser) {
            setUser(convertTimestamps(firestoreUser));
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        addToast('Error initializing authentication', { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Set up auth state listener
    const unsubscribe = FirebaseClient.onAuthStateChanged(async (firebaseUser: FirebaseAuthUser | null) => {
      if (firebaseUser) {
        try {
          const firestoreUser = await FirebaseClient.getUser(firebaseUser.uid);
          if (firestoreUser) {
            setUser(convertTimestamps(firestoreUser));
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          addToast('Error fetching user data', { type: 'error' });
        }
      } else {
        setUser(null);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [addToast]);

  const login = async (email: string, password: string, rememberMe = false): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const firebaseUser = await FirebaseClient.signInWithEmailAndPassword(email, password);
      if (firebaseUser) {
        const firestoreUser = await FirebaseClient.getUser(firebaseUser.uid);
        if (firestoreUser) {
          setUser(convertTimestamps(firestoreUser));
          addToast('Successfully logged in', { type: 'success' });
          return true;
        }
      }
      throw new Error('Failed to fetch user data');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      addToast(message, { type: 'error' });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await FirebaseClient.signOut();
      setUser(null);
      addToast('Successfully logged out', { type: 'success' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Logout failed';
      setError(message);
      addToast(message, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    email: string, 
    password: string, 
    additionalData: { 
      name: string; 
      role: UserRole;
      metadata?: {
        phoneNumber?: string;
        timezone?: string;
      };
    }
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const firebaseUser = await FirebaseClient.createUserWithEmailAndPassword(email, password, {
        name: additionalData.name,
        email,
        role: additionalData.role,
        metadata: additionalData.metadata
      });
      
      if (firebaseUser) {
        addToast('Account created successfully', { type: 'success' });
        return true;
      }
      throw new Error('Failed to create user');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
      addToast(message, { type: 'error' });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const checkPermission = (resource: ResourceType, action: Permission): boolean => {
    if (!user) return false;
    return hasPermission(user.role, action);
  };

  const refreshUser = async (): Promise<void> => {
    if (!user) return;
    try {
      const firestoreUser = await FirebaseClient.getUser(user.id);
      if (firestoreUser) {
        setUser(convertTimestamps(firestoreUser));
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
      addToast('Error refreshing user data', { type: 'error' });
    }
  };

  const contextValue = {
    user,
    login,
    logout,
    register,
    isAuthenticated: !!user,
    isLoading,
    error,
    checkPermission,
    refreshUser
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;
